import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  Shield, 
  Key, 
  Users, 
  Share2, 
  FileCode2,
  Flame,
  Wrench,
  Lock,
  Wallet,
  ChevronLeft,
  LayoutDashboard,
  PlusCircle,
  BookOpen,
  Plus,
  BarChart3,
  Activity,
  Network,
  ClipboardList,
  Layers,
  Image,
  Home,
  Droplets,
  FileText,
  DollarSign
} from 'lucide-react';
import { cn } from '@/lib/utils';

export function AdminLayout() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [buttonLayout, setButtonLayout] = useState<'Top' | 'Bottom'>(
    (localStorage.getItem('buttonLayout') as 'Top' | 'Bottom') || 'Top'
  );

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail === 'Top' || e.detail === 'Bottom') setButtonLayout(e.detail);
    };
    window.addEventListener('buttonLayoutChanged', handler as EventListener);
    return () => window.removeEventListener('buttonLayoutChanged', handler as EventListener);
  }, []);

  const location = useLocation();
  const [currentHash, setCurrentHash] = useState(location.hash);

  useEffect(() => {
    setCurrentHash(location.hash);
  }, [location.hash]);

  // Handle click outside navigation
  useEffect(() => {
    const handleClickOutside = (event) => {
      const nav = document.getElementById('admin-nav');
      if (nav && !nav.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Collapse nav when route changes
  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);
  
  const adminTabs = [
    {
      section: "Contract",
      items: [
        {
          title: "Analytics",
          path: "/admin/dashboard",
          icon: <BarChart3 className="w-4 h-4 text-[#0a74ff]" />,
          hash: "#analytics"
        },
        {
          title: "Fee Settings",
          path: "/admin/dashboard",
          icon: <DollarSign className="w-4 h-4 text-green-500" />,
          hash: "#feesetter"
        },
        {
          title: "Tokens",
          path: "/admin/dashboard",
          icon: <FileCode2 className="w-4 h-4 text-green-500" />,
          hash: "#tokens"
        },
        {
          title: "Contract",
          path: "/admin/dashboard",
          icon: <FileCode2 className="w-4 h-4 text-[#0a74ff]" />,
          hash: "#contract"
        },
        {
          title: "Ownership",
          path: "/admin/dashboard",
          icon: <Key className="w-4 h-4 text-yellow-500" />,
          hash: "#ownership"
        },
        {
          title: "Monitoring",
          path: "/admin/dashboard",
          icon: <Activity className="w-4 h-4 text-cyan-500" />,
          hash: "#monitoring"
        },
        {
          title: "Security",
          path: "/admin/dashboard",
          icon: <Shield className="w-4 h-4 text-purple-500" />,
          hash: "#security"
        },
        {
          title: "Network",
          path: "/admin/dashboard",
          icon: <Network className="w-4 h-4 text-blue-500" />,
          hash: "#network"
        },
        {
          title: "Reports",
          path: "/admin/dashboard",
          icon: <ClipboardList className="w-4 h-4 text-orange-500" />,
          hash: "#reporting"
        },
        {
          title: "Referrals",
          path: "/admin/dashboard",
          icon: <Share2 className="w-4 h-4 text-pink-500" />,
          hash: "#referrals"
        },
        {
          title: "Tiers",
          path: "/admin/dashboard",
          icon: <Layers className="w-4 h-4 text-emerald-500" />,
          hash: "#tiers"
        }
      ]
    }
  ];

  const renderContent = () => {
    switch (currentHash) {
      default:
        return <Outlet />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Admin Tabs Navigation */}
      <div 
        id="admin-nav"
        className={cn(
          "fixed left-0 bottom-0 bg-background/50 backdrop-blur-sm border-r border-[#0A74FF]/10 z-50 overflow-y-auto transition-all duration-300",
          buttonLayout === 'Bottom'
            ? "top-[calc(env(safe-area-inset-top)+var(--main-header-height))]"
            : "top-[calc(7.2rem+env(safe-area-inset-top))] md:top-[calc(7.5rem+env(safe-area-inset-top))] lg:top-[calc(8rem+env(safe-area-inset-top))]",
          isExpanded ? "w-64" : "w-14"
        )}
      >
        {/* Navigation content */}
        <nav className={cn(
          "flex flex-col space-y-2 pt-10",
          !isExpanded && "items-center"
        )}>
          {adminTabs.map((section) => (
            <div key={section.section} className="space-y-2">
              {isExpanded && (
                <div className="text-sm font-semibold text-muted-foreground px-3">
                  {section.section}
                </div>
              )}
              {section.items.map((tab) => {
                const isActive = location.pathname === tab.path && location.hash === tab.hash;
                
                return (
                  <Link
                    key={tab.title}
                    to={tab.path + tab.hash}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors border-2 overflow-hidden",
                      isActive
                        ? "bg-[#0A74FF]/10 text-[#FFD700] border-[#0A74FF]"
                        : "text-muted-foreground hover:text-[#FFD700] hover:bg-[#0A74FF]/5 border-transparent"
                    )}
                    title={tab.title}
                  >
                    <div className="flex-shrink-0">
                      {tab.icon}
                    </div>
                    <span className={cn(
                      "transition-all duration-300",
                      isExpanded ? "opacity-100 w-auto" : "opacity-0 w-0 hidden"
                    )}>
                      {tab.title}
                    </span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
      </div>

      {/* Toggle Button - Now outside and taller */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "fixed z-50 top-1/2 -translate-y-1/2 bg-background/50 backdrop-blur-sm hover:bg-[#0A74FF]/10 transition-all duration-300 h-24 flex items-center border border-[#0A74FF]/10",
          isExpanded ? "left-64" : "left-14",
          "px-1 rounded-r-lg"
        )}
      >
        <ChevronLeft className={cn(
          "w-5 h-5 text-[#0A74FF] transition-transform duration-300",
          !isExpanded && "rotate-180"
        )} />
      </button>

      <main className={cn(
        "flex-1 transition-all duration-200",
        buttonLayout === 'Bottom'
          ? "pt-[calc(env(safe-area-inset-top)+var(--main-header-height))]"
          : "pt-[calc(7.2rem+env(safe-area-inset-top))] md:pt-[calc(7.5rem+env(safe-area-inset-top))] lg:pt-[calc(8rem+env(safe-area-inset-top))]",
        isExpanded ? "pl-64" : "pl-14"
      )}>
        <div className="container mx-auto px-8">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
